import React, { useEffect, useState } from "react";
import { Box, CardContent, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, useMediaQuery, useTheme, Fade, Slide, Grid, MenuItem, Select, FormControl, InputLabel, Button, Tooltip, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { StyledTableCell, StyledTableRow, HeaderCard, SearchCard, StyledContainer } from "Pages/AdminList/ProfessionalUserList";
import AdminApi from "Service/Admin.api";
import PaymentApi from "Service/Payment.api";
import { useDispatch, useSelector } from "react-redux";
import { startLoading, stopLoading, setAlertData } from "../../Redux/Screen/Action";
import { formatDate, getStatusColorHex } from "Utils/Common";
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { StatusChip } from "Pages/OrderHistory/OrderHistory";

interface MergedHistoryType {
  pkCase: string;
  caseNumber: string;
  orderTypeId?: number;
  orderTypeName: string;
  caseTypeName: string;
  billingCycle?: string;
  assignedProfessionalName: string;
  clientFullName: string;
  clientEmail: string;
  clientContactNumber: string;
  clientOrganizationName?: string;
  casePrice: number;
  currentProcessingStatusName: string;
  casePaymentStatus: string;
  dateCreated: string;

  odrStatus?: string;
  odrCustomStatus?: string;
  odrType?: string;
  parentCaseId?: number;
  isDeleted?: boolean;

  pkPayment?: number;
  transactionId?: string;
  paymentType?: number; // 1 for Charge, 2 for Refund (usually)
  paymentStatus?: string;
  isRefundable?: boolean;
}

const OrderAndPaymentHistory: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const { myInfo } = useSelector((state: any) => state?.user || {});

  const [mergedData, setMergedData] = useState<MergedHistoryType[]>([]);
  const [filteredData, setFilteredData] = useState<MergedHistoryType[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("All");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userType, setUserType] = useState<string | null>(null);

  const [refundReason, setRefundReason] = useState("");
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [paymentToRefund, setPaymentToRefund] = useState<MergedHistoryType | null>(null);

  const fetchData = async () => {
    dispatch(startLoading({ loading: true, loadtext: "Fetching Data..." }));

    try {
      let uType = myInfo?.userType;
      let userId = myInfo?.id;

      if (!userId || !uType) {
        const persistedData = localStorage.getItem("persist:root");
        if (persistedData) {
          try {
            const parsedRoot = JSON.parse(persistedData);
            const userState = typeof parsedRoot.user === "string" ? JSON.parse(parsedRoot.user) : parsedRoot.user;
            const info = userState?.myInfo || userState;
            uType = info?.userType || uType;
            userId = info?.id || userId;
          } catch (e) {
            console.error("Error reading fallback user from localStorage", e);
          }
        }
      }

      setUserType(uType);

      // Call single consolidated merged-history endpoint
      const res = await PaymentApi.getMergedHistory(userId, 1, 10000, "", "All");
      let data: MergedHistoryType[] = [];

      if (res?.success && res?.data) {
        data = res.data.data || res.data || [];
      } else if (Array.isArray(res?.data)) {
        data = res.data;
      }

      setMergedData(data);
      setFilteredData(data);

    } catch (error) {
      console.error("Failed to fetch merged history:", error);
    } finally {
      dispatch(stopLoading());
    }
  };

  useEffect(() => {
    fetchData();
  }, [myInfo]);

  useEffect(() => {
    let result = mergedData;

    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item =>
        String(item.caseNumber).toLowerCase().includes(query) ||
        String(item.transactionId).toLowerCase().includes(query) ||
        item.clientFullName?.toLowerCase().includes(query) ||
        item.clientEmail?.toLowerCase().includes(query) ||
        item.clientContactNumber?.toLowerCase().includes(query) ||
        item.orderTypeName?.toLowerCase().includes(query) ||
        item.caseTypeName?.toLowerCase().includes(query) ||
        item.assignedProfessionalName?.toLowerCase().includes(query) ||
        item.casePrice?.toString().toLowerCase().includes(query) ||
        item.currentProcessingStatusName?.toLowerCase().includes(query) ||
        item.casePaymentStatus?.toLowerCase().includes(query) ||
        item.dateCreated?.toLowerCase().includes(query) ||
        (item.isDeleted && ("deleted".includes(query) || "archived".includes(query)))
      );
    }

    // Payment status filter
    if (paymentStatusFilter !== "All") {
      result = result.filter(item => {
        if (paymentStatusFilter === "Archived") {
          return item.isDeleted === true;
        }

        const itemStatus = item.pkPayment ? (item.paymentStatus || "Pending") : (item.casePaymentStatus || "Pending");
        const statusLower = itemStatus.toLowerCase();
        const isPaid = statusLower === "success" || statusLower === "paymentcompleted" || statusLower === "completed" || Number(item.casePrice) === 0;

        if (paymentStatusFilter === "Success") {
          return isPaid;
        }
        if (paymentStatusFilter === "Failed") {
          return !isPaid && (statusLower === "failed" || statusLower === "rejected" || statusLower === "paymentfailed" || statusLower === "paymentnotcompleted");
        }
        if (paymentStatusFilter === "Pending") {
          return !isPaid && (statusLower === "pending" || statusLower === "paymentpending" || statusLower === "paymentnotcompleted" || statusLower === "initiated");
        }
        return true;
      });
    }

    setFilteredData(result);
    setPage(0);
  }, [searchQuery, paymentStatusFilter, mergedData]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefund = (payment: MergedHistoryType) => {
    setPaymentToRefund(payment);
    setIsRefundModalOpen(true);
  };

  const initiateRefundApi = async () => {
    if (!paymentToRefund) return;

    dispatch(startLoading({ loading: true, loadtext: "Processing refund..." }));

    try {
      if (!refundReason.trim()) {
        dispatch(stopLoading());
        dispatch(setAlertData({
          alertMessage: "Please add a reason for the refund.",
          alertVariant: "error",
          openAlert: true
        }));
        return;
      }

      const refundPayload = {
        caseId: paymentToRefund.pkCase,
        transactionId: paymentToRefund.transactionId,
        refundReason: refundReason,
        paymentId: paymentToRefund.pkPayment?.toString() || "",
        amount: paymentToRefund.casePrice,
      };

      const response = await AdminApi.RefundRequest(refundPayload);
      dispatch(setAlertData({
        alertMessage: "Refund processed successfully for Payment ID: " + paymentToRefund.pkPayment,
        alertVariant: "success",
        openAlert: true
      }));

      fetchData();
      setIsRefundModalOpen(false);
      setPaymentToRefund(null);
    } catch (error) {
      console.error("Refund failed:", error);
      dispatch(stopLoading());
      dispatch(setAlertData({
        alertMessage: "Refund failed. Please check the network logs and server response.",
        alertVariant: "error",
        openAlert: true
      }));
    } finally {
      dispatch(stopLoading());
    }
  };

  const currentTableData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const renderTable = () => (
    <TableContainer
      component={Paper}
      sx={{
        mt: 2,
        boxShadow: "0 10px 15px -3px rgba(0,0,0,0.05)",
        bgcolor: "#ffffff",
        borderRadius: "16px",
        overflowX: "auto",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <StyledTableCell>Case No.</StyledTableCell>
            <StyledTableCell>Transaction ID</StyledTableCell>
            <StyledTableCell>Order Type</StyledTableCell>
            <StyledTableCell>Case Type</StyledTableCell>
            <StyledTableCell>Assigned Professional</StyledTableCell>
            {(userType === "ADMIN" || userType === "SUPERADMIN" || userType?.toLowerCase() === "e_mitra_user") && (
              <StyledTableCell>Client Details</StyledTableCell>
            )}
            {userType !== "PROFESSIONAL" && (
              <StyledTableCell>Amount</StyledTableCell>
            )}
            <StyledTableCell>Payment Type</StyledTableCell>
            <StyledTableCell>Case Status</StyledTableCell>
            <StyledTableCell>Payment Status</StyledTableCell>
            <StyledTableCell>Created At</StyledTableCell>
            {userType === "ADMIN" && (
              <StyledTableCell>Actions</StyledTableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {currentTableData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                <Typography variant="h6" color="textSecondary">No data found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            currentTableData.map((row) => {
              let finalPaymentStatus = row.pkPayment
                ? (row.paymentStatus || "Pending")
                : (row.casePaymentStatus || "Pending");

              if (Number(row.casePrice) === 0) {
                finalPaymentStatus = "Not Applicable";
              }

              return (
                <StyledTableRow 
                  key={row.pkPayment ? `pay-${row.pkPayment}` : `case-${row.pkCase}`}
                  sx={{ opacity: row.isDeleted ? 0.85 : 1, backgroundColor: row.isDeleted ? "#FAF9F6" : "inherit" }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" sx={{ textDecoration: row.isDeleted ? "line-through" : "none" }}>
                        {row.caseNumber}
                      </Typography>
                      {row.isDeleted && (
                        <StatusChip
                          label="Archived"
                          status="Archived"
                          sx={{
                            background: "#F3F4F6",
                            color: "#4B5563",
                            border: "1px solid #D1D5DB",
                            fontSize: "0.7rem",
                            height: 20,
                            px: 0.5
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{row.transactionId}</TableCell>
                  <TableCell>{row.orderTypeName}</TableCell>
                  <TableCell>{row.caseTypeName}</TableCell>
                  <TableCell>
                    {row.assignedProfessionalName ? row.assignedProfessionalName : <Typography color="textSecondary" variant="body2">Not Assigned</Typography>}
                  </TableCell>
                  {(userType === "ADMIN" || userType === "SUPERADMIN" || userType?.toLowerCase() === "e_mitra_user") && (
                    <TableCell>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="subtitle2" fontWeight="bold">{row.clientFullName || "N/A"}</Typography>
                        <Typography variant="caption" color="textSecondary">{row.clientEmail || "N/A"}</Typography>
                        <Typography variant="caption" color="textSecondary">{row.clientContactNumber || "N/A"}</Typography>
                      </Box>
                    </TableCell>
                  )}
                  {userType !== "PROFESSIONAL" && (
                    <TableCell>
                      <Typography fontWeight="bold" color="primary">₹{row.casePrice}</Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    {row.paymentType ? (
                      <StatusChip
                        label={row.paymentType === 1 ? "Charge" : "Refund"}
                        sx={{
                          background: getStatusColorHex(row.paymentType === 1 ? "Charge" : "Refund").bg,
                          color: getStatusColorHex(row.paymentType === 1 ? "Charge" : "Refund").text,
                        }}
                      />
                    ) : (
                      "N/A"
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={row.isDeleted ? "Archived" : (row.currentProcessingStatusName || "Pending")}
                      status={row.isDeleted ? "Archived" : (row.currentProcessingStatusName || "Pending")}
                      sx={{
                        background: getStatusColorHex(row.isDeleted ? "Archived" : (row.currentProcessingStatusName || "Pending")).bg,
                        color: getStatusColorHex(row.isDeleted ? "Archived" : (row.currentProcessingStatusName || "Pending")).text,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={finalPaymentStatus}
                      status={finalPaymentStatus}
                      sx={{
                        background: getStatusColorHex(finalPaymentStatus).bg,
                        color: getStatusColorHex(finalPaymentStatus).text,
                      }}
                    />
                  </TableCell>
                  <TableCell>{formatDate(row.dateCreated)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {userType === "ADMIN" &&
                        row.paymentType === 1 &&
                        row.isRefundable === true && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handleRefund(row)}
                            disabled={
                              !(finalPaymentStatus === "Success" ||
                                finalPaymentStatus === "PaymentCompleted" || finalPaymentStatus?.toLowerCase() === "completed")
                            }
                          >
                            Request Refund
                          </Button>
                        )}
                    </Box>
                  </TableCell>
                </StyledTableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <StyledContainer maxWidth="xl">
      <Fade in timeout={800}>
        <Box sx={{ width: '100%', pb: 10 }}>
          {/* Header Section */}
          <HeaderCard elevation={8} sx={{ mb: 4 }}>
            <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
              <Box display="flex" alignItems="center" gap={2}>
                <ReceiptLongIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56 } }} />
                <Box>
                  <Typography variant="h4" component="h1" fontWeight="bold" color="white" gutterBottom>
                    Order and Payment History
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }} color="white">
                    View your complete merged order details and corresponding transactions.
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </HeaderCard>

          {/* Filters Section */}
          <SearchCard elevation={2} sx={{ mb: 4 }}>
            <CardContent sx={{ p: '20px !important' }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <TextField
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by Case No, Transaction ID or Client details..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}>
                    <InputLabel>Payment Status</InputLabel>
                    <Select
                      value={paymentStatusFilter}
                      label="Payment Status"
                      onChange={(e) => setPaymentStatusFilter(e.target.value)}
                    >
                      <MenuItem value="All">All Payments</MenuItem>
                      <MenuItem value="Success">Success / Completed</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Failed">Failed / Rejected</MenuItem>
                      <MenuItem value="Archived">Archived / Deleted</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </SearchCard>

          <Slide direction="up" in timeout={600}>
            <Box>
              {renderTable()}
              {filteredData.length > 0 && (
                <TablePagination
                  component="div"
                  count={filteredData.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                />
              )}
            </Box>
          </Slide>
        </Box>
      </Fade>

      {/* Refund Confirmation Modal */}
      <Dialog
        open={isRefundModalOpen}
        onClose={() => {
          setIsRefundModalOpen(false);
          setRefundReason("");
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: theme.palette.error.main, color: 'white', fontWeight: 'bold' }}>
          Confirm Refund Processing
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText sx={{ color: "text.primary", mb: 3 }}>
            You are about to initiate a refund for <strong>₹{paymentToRefund?.casePrice}</strong>.
            Please provide a valid reason for this refund request.
          </DialogContentText>

          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            label="Reason for Refund"
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            placeholder="Detailed explanation for the refund..."
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
              '& .Mui-focused fieldset': { borderColor: theme.palette.primary.main }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setIsRefundModalOpen(false);
              setRefundReason("");
            }}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => initiateRefundApi()}
            variant="contained"
            color="error"
            disabled={!refundReason.trim()}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Yes, Process Refund
          </Button>
        </DialogActions>
      </Dialog>
    </StyledContainer>
  );
};

export default OrderAndPaymentHistory;
